// Run-completion notification helpers (0027 P6).

import type { RunStreamState } from "../hooks/useRunStream";

// isRetrySafeCategory reports whether an error snapshot's category is in the
// set of transient failures a Retry was meant for. Read by the first-run
// RunProgress error view's Retry gate. No run_error carries a category: the
// only one an error snapshot holds is the client's own STREAM_UNAVAILABLE
// (useRunStream's giveUp), which is not in the set, so this is never true
// today and that one Retry button is unreachable (pre-existing: the dead-Retry
// forward item noted at ErrorState in components/RunProgress.tsx). It says
// nothing about a refund: a surface claims one only when the snapshot's
// refunded is true.
export function isRetrySafeCategory(category: string | undefined): boolean {
  return category
    ? ["transient_llm", "transient_network", "rate_limited", "engine_unavailable"].includes(category)
    : false;
}

// The two founder-ruled lines for the rename while a pass is in flight (0159
// F1 (a)). This file is their ONE home: usePatchProductTitle imports them and
// does not spell them. RUN_IN_FLIGHT_LINE is the hint beside a held act and
// the line for a stale tab's `.run_in_flight` refusal; WRITE_CONFLICT_LINE is
// the rare commit race (`product.write_conflict`: the tree changed between the
// act's read and its commit).
// 0051 slice 2: RUN_IN_FLIGHT_LINE is FD-1's present-tense line (ruling 2: no unit word).
export const RUN_IN_FLIGHT_LINE = "We're working on your spec, so this can't be done right now.";
export const WRITE_CONFLICT_LINE = "This changed while you were saving. Reload and try again.";

// STREAM_UNAVAILABLE is the error snapshot's ONE category (0159): the client
// gave up following the run (a new session is needed, or the server says the
// product or run is gone). useRunStream's giveUp is its only writer; the
// server never sets a category.
export const STREAM_UNAVAILABLE = "stream_unavailable";

// The three readings of a run's error end (0159 F4). ONE predicate per
// condition: the store's revive rule, the headings and every run surface
// (the first-run view's eyebrow, the toast, the run menu) call these, never
// the raw fields.
//   - isClientGiveUp: the client gave up following the run. Not a run end
//     the server reported: the run may still be running or may have
//     succeeded.
//   - readsInterrupted: the server reported the run interrupted (outcome
//     interrupted).
//   - readsFailed: any other error - the server reported the run failed
//     (outcome failed), or a run_error with no outcome the client knows
//     (every run_error carries one since 0159-5). Only this reading says
//     "failed".
export function isClientGiveUp(s: RunStreamState): boolean {
  return s.phase === "error" && s.category === STREAM_UNAVAILABLE;
}

export function readsInterrupted(s: RunStreamState): boolean {
  return s.phase === "error" && s.outcome === "interrupted";
}

export function readsFailed(s: RunStreamState): boolean {
  return s.phase === "error" && !isClientGiveUp(s) && !readsInterrupted(s);
}

// F4 (a)'s two ruled lines (0159), defined here once: (i) a client give-up,
// (ii) a server-reported interruption. (0051 slice 2: FD-1 (c), no unit word.)
const LOST_TRACK_LINE = "We lost track of this. Open History to see how it ended.";
const STOPPED_LINE = "This stopped before it finished.";

// runEndLine is F4 (a)'s line for an error end that is not a failure: (i) for
// a client give-up, (ii) for a server-reported interruption; null for a
// failed run (its surface keeps its own "failed" wording) and for a snapshot
// that is not an error.
export function runEndLine(s: RunStreamState): string | null {
  if (isClientGiveUp(s)) return LOST_TRACK_LINE;
  if (readsInterrupted(s)) return STOPPED_LINE;
  return null;
}

// titledRunEndLine is runEndLine prefixed with the product's title in quotes,
// for a surface that names the product (the toast, the run menu): the titled
// pattern of the Review page's notice (hooks/useQueues refusalNoticeLine).
// null where runEndLine is null.
export function titledRunEndLine(title: string, s: RunStreamState): string | null {
  const line = runEndLine(s);
  return line === null ? null : `"${title}": ${line}`;
}

// runErrorHeading is the heading of a run's error surface, saying what the
// server knows (0159 F4 (a)): runEndLine's line for a client give-up or a
// server-reported interruption; anything else reads the caller's own heading
// (the first-run view's "Something tripped.").
export function runErrorHeading(snapshot: RunStreamState, fallback: string): string {
  return runEndLine(snapshot) ?? fallback;
}

// shouldNotifyBrowser is the pure gate for firing an OS-level browser
// Notification when a run completes. We only notify when the founder has
// opted in, granted permission, the tab is NOT the focused one (when it's
// visible the RunPill + recap already inform them — an OS popup on top is
// noise). Once per run is the caller's guard (RunCompletionNotifier's notifiedRef).
export function shouldNotifyBrowser(opts: {
  prefEnabled: boolean;
  permission: NotificationPermission;
  documentHidden: boolean;
}): boolean {
  return (
    opts.prefEnabled &&
    opts.permission === "granted" &&
    opts.documentHidden
  );
}

// notificationsNeedPermission reports whether the "notify on completion" pref
// is ON but this browser hasn't granted OS Notification permission — i.e. the
// Settings toggle looks enabled yet `shouldNotifyBrowser` can never pass
// (it requires permission === "granted"). The pref is server-persisted and
// per-user; permission is per-browser, so a founder who enabled it on one
// device (or before a permission reset) lands here with a checked-but-dead
// toggle. Drives the Settings "enable in this browser" affordance so the UI
// stops silently lying. "unsupported" is excluded — there's nothing to grant.
export function notificationsNeedPermission(opts: {
  prefEnabled: boolean;
  permission: NotificationPermission | "unsupported";
}): boolean {
  return (
    opts.prefEnabled &&
    opts.permission !== "granted" &&
    opts.permission !== "unsupported"
  );
}

// playCompletionChime plays a short, calm two-note cue via the Web Audio
// API (no asset to ship). Best-effort: silently no-ops if Web Audio is
// unavailable or blocked by autoplay policy.
export function playCompletionChime(): void {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [
      { f: 587.33, t: 0 }, // D5
      { f: 880.0, t: 0.12 }, // A5
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = n.f;
      gain.gain.setValueAtTime(0.0001, now + n.t);
      gain.gain.exponentialRampToValueAtTime(0.12, now + n.t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + n.t + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + n.t);
      osc.stop(now + n.t + 0.2);
    }
    setTimeout(() => void ctx.close(), 600);
  } catch {
    // no-op — audio is a nicety, never a failure path
  }
}
